import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrintEntryButton } from "@/components/PrintEntryButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Trash2, Pencil, Paperclip, Link2, FilePlus2, ExternalLink, Megaphone, Globe, Bell, FileText, Download, UserPlus, Clock, CheckCircle2, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import { Field, Empty } from "@/pages/Vehicles";
import { FileUpload } from "@/components/FileUpload";

const CATEGORIES = [
  "Operator Licence", "Insurance", "Health & Safety", "Environmental / Waste",
  "Policies & Procedures", "Certificates", "HR / Employment", "Financial", "Web Link", "Other",
];
const empty = { title: "", category: "Operator Licence", reference: "", expiry_date: "", link_url: "", notes: "", attachments: [] };

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileHref = (att) => `${BACKEND}/api/files/${att.file_id}?auth=${localStorage.getItem("token") || ""}`;
const globalFileHref = (att) => `${BACKEND}/api/global-docs/files/${att.file_id}?auth=${localStorage.getItem("token") || ""}`;

const GLOBAL_CATEGORIES = ["Guidance", "Legislation", "DVSA / RSA Notice", "Safety Alert", "Best Practice", "Template", "Other"];
const gEmpty = { title: "", category: "Guidance", link_url: "", notes: "", attachments: [] };

function SignOffsSection() {
  const [templates, setTemplates] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [busyKey, setBusyKey] = useState("");
  const [target, setTarget] = useState(null); // { source_type, source_key/source_file_id, title }
  const [selDriver, setSelDriver] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [fileUp, setFileUp] = useState([]);

  const loadAll = () => {
    api.get("/form-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/drivers").then((r) => setDrivers((r.data || []).filter((d) => !d.leave_date))).catch(() => {});
    api.get("/form-assignments").then((r) => setAssignments(r.data || [])).catch(() => {});
  };
  useEffect(loadAll, []);

  const openPdf = async (url) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(u, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Could not open PDF"); }
  };
  const viewTemplate = async (t) => { setBusyKey(t.key); await openPdf(`/form-templates/${t.key}/download`); setBusyKey(""); };

  const openAssignTemplate = (t) => { setTarget({ source_type: "template", source_key: t.key, title: t.title }); setSelDriver(""); setNote(""); setFileUp([]); };
  const openAssignFile = () => { setTarget({ source_type: "file", title: "" }); setSelDriver(""); setNote(""); setFileUp([]); };

  const submitAssign = async () => {
    if (!selDriver) { toast.error("Pick a driver"); return; }
    const body = { driver_id: selDriver, source_type: target.source_type, note, title: target.title || "" };
    if (target.source_type === "template") body.source_key = target.source_key;
    if (target.source_type === "file") {
      if (!fileUp?.[0]?.file_id) { toast.error("Upload a file to send"); return; }
      body.source_file_id = fileUp[0].file_id;
      body.title = target.title || fileUp[0].filename || "Document";
    }
    setSaving(true);
    try {
      await api.post("/form-assignments", body);
      toast.success("Sent to the driver to sign");
      setTarget(null); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setSaving(false);
  };

  const removeAssignment = async (a) => {
    if (!window.confirm("Remove this assignment?")) return;
    try { await api.delete(`/form-assignments/${a.id}`); toast.success("Removed"); loadAll(); }
    catch { toast.error("Could not remove"); }
  };

  const pending = assignments.filter((a) => a.status !== "signed");
  const signed = assignments.filter((a) => a.status === "signed");

  return (
    <div className="mb-8" data-testid="signoffs-section">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center"><FileText size={16} /></span>
          <div>
            <h3 className="font-heading font-bold text-slate-900 tracking-tight leading-tight">Forms, Templates &amp; e-Signing</h3>
            <p className="text-xs text-slate-400">Download forms, or send any document to a driver to complete &amp; sign in their app</p>
          </div>
        </div>
        <Button data-testid="assign-own-file" onClick={openAssignFile} variant="outline" className="gap-2 border-slate-300"><Upload size={15} /> Send your own file to sign</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.key} data-testid="form-template-card" className="bg-white border border-slate-200 rounded-md p-5 flex flex-col animate-in-up">
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">{t.category}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{t.region}</span>
            </div>
            <h4 className="font-heading font-bold text-slate-900 leading-tight mt-1 break-words">{t.title}</h4>
            <p className="text-sm text-slate-500 mt-1.5 flex-1">{t.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="download-form-template" onClick={() => viewTemplate(t)} disabled={busyKey === t.key} variant="outline" className="gap-2 border-slate-300"><Download size={15} /> {busyKey === t.key ? "Opening…" : "Download / print"}</Button>
              <Button data-testid="assign-form-template" onClick={() => openAssignTemplate(t)} className="gap-2 bg-slate-900 hover:bg-slate-800"><UserPlus size={15} /> Assign to driver</Button>
            </div>
          </div>
        ))}
      </div>

      {assignments.length > 0 && (
        <div className="mt-6" data-testid="assignments-list">
          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Out for signature</h4>
          <div className="space-y-2">
            {[...pending, ...signed].map((a) => (
              <div key={a.id} data-testid="assignment-row" className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{a.title}</p>
                  <p className="text-xs text-slate-500">To: {a.driver_name || "driver"} · assigned {String(a.assigned_at).slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {a.status === "signed"
                    ? <span data-testid="assignment-signed" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 size={11} /> Signed</span>
                    : <span data-testid="assignment-pending" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Clock size={11} /> Pending</span>}
                  {a.status === "signed" && <Button data-testid="assignment-download" size="sm" variant="outline" onClick={() => openPdf(`/form-assignments/${a.id}/signed`)} className="gap-1.5 border-slate-300"><Download size={13} /> Signed PDF</Button>}
                  <button data-testid="assignment-delete" onClick={() => removeAssignment(a)} className="p-2 rounded-md text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus size={18} /> Assign to a driver</DialogTitle>
            <DialogDescription>The driver will see this in their app and can read it, then sign it electronically. You'll see it here once signed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {target?.source_type === "file" && (
              <Field label="Document to send (PDF)">
                <FileUpload testid="assign-file-upload" accept="application/pdf" label="Upload the PDF to be signed" attachments={fileUp} onChange={setFileUp} />
              </Field>
            )}
            {target?.source_type !== "file" && <p className="text-sm text-slate-600"><span className="font-semibold">Document:</span> {target?.title}</p>}
            <Field label="Driver">
              <Select value={selDriver} onValueChange={setSelDriver}>
                <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Choose a driver" /></SelectTrigger>
                <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Note to driver (optional)"><Textarea data-testid="assign-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please read and sign by Friday" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button data-testid="assign-submit" onClick={submitAssign} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2"><Send size={15} /> {saving ? "Sending…" : "Send to driver"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SharedDocs() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(gEmpty);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/global-docs"); setItems(r.data); } catch { /* ignore */ } };
  useEffect(() => {
    load();
    api.post("/global-docs/mark-seen").then(() => window.dispatchEvent(new Event("shared-docs-seen"))).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Enter a title");
    setBusy(true);
    try {
      await api.post("/admin/global-docs", form);
      toast.success("Shared with all HaulCheck users");
      setOpen(false); setForm(gEmpty); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not share document"); }
    setBusy(false);
  };
  const remove = async (id) => { try { await api.delete(`/admin/global-docs/${id}`); setItems((m) => m.filter((x) => x.id !== id)); } catch { toast.error("Could not remove"); } };

  if (items.length === 0 && !isAdmin) return null;

  return (
    <div className="mb-8" data-testid="shared-docs-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Megaphone size={16} /></span>
          <div>
            <h3 className="font-heading font-bold text-slate-900 tracking-tight leading-tight">Shared by HaulCheck</h3>
            <p className="text-xs text-slate-400">Compliance guidance & resources for every operator</p>
          </div>
        </div>
        {isAdmin && <Button data-testid="add-shared-doc-button" onClick={() => { setForm(gEmpty); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 rounded-md gap-2 shrink-0"><FilePlus2 size={15} /> Add shared doc</Button>}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-md p-6 text-center text-sm text-slate-400" data-testid="shared-docs-empty">No shared resources yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((d) => (
            <div key={d.id} data-testid="shared-doc-card" className="bg-slate-900 text-white rounded-md p-5 flex flex-col animate-in-up">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">{d.category}</span>
                  <h4 className="font-heading font-bold leading-tight mt-0.5 break-words">{d.title}</h4>
                </div>
                {isAdmin && <button data-testid="delete-shared-doc" onClick={() => remove(d.id)} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={15} /></button>}
              </div>
              {d.notes && <p className="text-sm text-slate-300 mt-2 whitespace-pre-line">{d.notes}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-auto pt-3">
                {d.link_url && (
                  <a data-testid="open-shared-link" href={d.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:underline">
                    <Link2 size={13} /> Open link <ExternalLink size={11} />
                  </a>
                )}
                {(d.attachments || []).map((a, i) => (
                  <a key={i} data-testid="open-shared-file" href={globalFileHref(a)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 hover:text-white">
                    <Paperclip size={13} /> {a.filename || `File ${i + 1}`}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="shared-doc-dialog">
          <DialogHeader><DialogTitle className="font-heading">Share a compliance doc / link</DialogTitle>
            <DialogDescription>This appears for <b>every registered HaulCheck user</b> to help everyone stay compliant.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Title *"><Input data-testid="sd-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. DVSA Guide to Maintaining Roadworthiness" /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="sd-category"><SelectValue /></SelectTrigger>
                <SelectContent>{GLOBAL_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Web link (optional)"><Input data-testid="sd-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://www.gov.uk/…" /></Field>
            <Field label="Notes"><Textarea data-testid="sd-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Short description of what this is and why it matters" /></Field>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Attachments</p>
              <FileUpload testid="sd-files" label="Upload files" attachments={form.attachments} onChange={(a) => setForm({ ...form, attachments: a })} />
            </div>
            <DialogFooter><Button data-testid="save-shared-doc-button" type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 gap-2"><Globe size={15} /> {busy ? "Sharing…" : "Share with everyone"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const UPDATE_CATEGORIES = ["Notice", "Policy", "Bulletin", "Toolbox Talk", "Memo", "Other"];
const uEmpty = { title: "", category: "Notice", notes: "", link_url: "", attachments: [] };

function CompanyUpdates() {
  const { user } = useAuth();
  const canUpload = user?.role !== "viewer";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(uEmpty);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await api.get("/company-updates"); setItems(r.data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Enter a title");
    setBusy(true);
    try { await api.post("/company-updates", form); toast.success("Update shared with your office"); setOpen(false); setForm(uEmpty); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not save update"); }
    setBusy(false);
  };
  const remove = async (id) => { try { await api.delete(`/company-updates/${id}`); setItems((m) => m.filter((x) => x.id !== id)); } catch { toast.error("Could not remove"); } };

  return (
    <div className="mb-8" data-testid="company-updates-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><Bell size={16} /></span>
          <div>
            <h3 className="font-heading font-bold text-slate-900 tracking-tight leading-tight">Updates</h3>
            <p className="text-xs text-slate-400">Notices &amp; files shared with everyone in your company — private to your office</p>
          </div>
        </div>
        {canUpload && <Button data-testid="add-company-update-button" onClick={() => { setForm(uEmpty); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 rounded-md gap-2 shrink-0"><FilePlus2 size={15} /> Add update</Button>}
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-md p-6 text-center text-sm text-slate-400" data-testid="company-updates-empty">No updates yet. Share a notice or file with your office.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((d) => (
            <div key={d.id} data-testid="company-update-card" className="bg-white border border-slate-200 rounded-md p-5 flex flex-col animate-in-up">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">{d.category}</span>
                  <h4 className="font-heading font-bold leading-tight mt-0.5 break-words text-slate-900">{d.title}</h4>
                </div>
                {canUpload && <button data-testid="delete-company-update" onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600 shrink-0"><Trash2 size={15} /></button>}
              </div>
              {d.notes && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{d.notes}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-auto pt-3">
                {d.link_url && <a data-testid="open-update-link" href={d.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><Link2 size={13} /> Open link <ExternalLink size={11} /></a>}
                {(d.attachments || []).map((a, i) => (<a key={i} data-testid="open-update-file" href={fileHref(a)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"><Paperclip size={13} /> {a.filename || `File ${i + 1}`}</a>))}
              </div>
              {d.uploaded_by && <p className="text-[11px] text-slate-400 mt-2">Shared by {d.uploaded_by}</p>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="company-update-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add an update</DialogTitle><DialogDescription>Shared with <b>everyone in your company</b> and kept strictly private to your office.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Title *"><Input data-testid="cu-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. New tacho download procedure" /></Field>
            <Field label="Category"><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="cu-category"><SelectValue /></SelectTrigger><SelectContent>{UPDATE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Web link (optional)"><Input data-testid="cu-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://…" /></Field>
            <Field label="Notes"><Textarea data-testid="cu-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What your team needs to know" /></Field>
            <div><p className="text-sm font-medium text-slate-700 mb-1.5">Attachments</p><FileUpload testid="cu-files" label="Upload files" attachments={form.attachments} onChange={(a) => setForm({ ...form, attachments: a })} /></div>
            <DialogFooter><Button data-testid="save-company-update-button" type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">{busy ? "Sharing…" : "Share update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const dayStatus = (dateStr) => {
  if (!dateStr) return null;
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (days < 0) return { cls: "bg-red-100 text-red-700", label: "Expired" };
  if (days <= 30) return { cls: "bg-amber-100 text-amber-700", label: `${days}d left` };
  return { cls: "bg-green-100 text-green-700", label: "Valid" };
};

export function ComplianceDocsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("All");

  const load = async () => {
    const r = await api.get("/compliance-docs");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...empty, ...d, expiry_date: d.expiry_date || "", attachments: d.attachments || [] }); setEditId(d.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Enter a title");
    let link = form.link_url.trim();
    if (link && !/^https?:\/\//i.test(link)) link = `https://${link}`;
    const payload = { ...form, link_url: link, expiry_date: form.expiry_date || null };
    try {
      if (editId) await api.put(`/compliance-docs/${editId}`, payload);
      else await api.post("/compliance-docs", payload);
      toast.success(editId ? "Document updated" : "Document added");
      setOpen(false); load();
    } catch { toast.error("Could not save document"); }
  };
  const remove = async (id) => { await api.delete(`/compliance-docs/${id}`); toast.success("Document deleted"); load(); };

  const categories = ["All", ...CATEGORIES.filter((c) => items.some((i) => i.category === c))];
  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="compliance-docs-page">
      <SignOffsSection />
      <SharedDocs />
      <CompanyUpdates />
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <p className="text-sm text-slate-500">Store compliance documents and quick web links — operator licence, policies, certificates, portals and more.</p>
        <Button data-testid="add-compliance-doc-button" onClick={openNew} className="bg-black hover:bg-slate-800 rounded-md gap-2 shrink-0"><FilePlus2 size={16} /> Add Document / Link</Button>
      </div>

      {categories.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {categories.map((c) => (
            <button key={c} data-testid={`cd-filter-${c}`} onClick={() => setFilter(c)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === c ? "bg-black text-white border-black" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {c}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <Empty icon={ShieldCheck} text="No compliance documents yet. Add operator licence paperwork, policies, certificates or a useful web link." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((d) => {
            const st = dayStatus(d.expiry_date);
            return (
              <div key={d.id} data-testid="compliance-doc-card" className="bg-white border border-slate-200 rounded-md p-5 animate-in-up flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{d.category}</span>
                    <h4 className="font-heading font-bold text-slate-900 leading-tight mt-0.5 break-words">{d.title}</h4>
                    {d.reference && <p className="text-xs text-slate-500 mt-0.5">Ref: {d.reference}</p>}
                  </div>
                  <div className="flex items-center shrink-0">
                    <button data-testid="edit-compliance-doc-button" onClick={() => openEdit(d)} className="text-slate-400 hover:text-slate-900 p-1.5"><Pencil size={15} /></button>
                    <div className="flex items-center gap-1"><PrintEntryButton kind="compliance-doc" id={d.id} hasFiles={d.attachments?.length > 0} variant="icon" /><button data-testid="delete-compliance-doc-button" onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={15} /></button></div>
                  </div>
                </div>

                {d.notes && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{d.notes}</p>}

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {d.expiry_date && st && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>Expires {d.expiry_date} · {st.label}</span>}
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-auto pt-3">
                  {d.link_url && (
                    <a data-testid="open-compliance-link" href={d.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline">
                      <Link2 size={13} /> Open link <ExternalLink size={11} />
                    </a>
                  )}
                  {(d.attachments || []).map((a, i) => (
                    <a key={i} data-testid="open-compliance-file" href={fileHref(a)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900">
                      <Paperclip size={13} /> {a.filename || `File ${i + 1}`}
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Document" : "Add Document / Link"}</DialogTitle>
            <DialogDescription className="sr-only">Compliance document form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Title *"><Input data-testid="cd-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Operator Licence disc, H&S Policy" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="cd-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Reference"><Input data-testid="cd-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Doc / policy no." /></Field>
            </div>
            <Field label="Expiry / review date"><Input data-testid="cd-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
            <Field label="Web link (optional)"><Input data-testid="cd-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://…" /></Field>
            <Field label="Notes"><Textarea data-testid="cd-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></Field>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Attachments</p>
              <FileUpload testid="cd-files" label="Upload files" attachments={form.attachments} onChange={(a) => setForm({ ...form, attachments: a })} />
            </div>
            <DialogFooter><Button data-testid="save-compliance-doc-button" type="submit" className="bg-black hover:bg-slate-800">{editId ? "Save Changes" : "Add Document"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ComplianceDocs() { return <ComplianceDocsPanel />; }
